import React, { useState, useCallback } from "react";
import { ChatArea, ChatInput } from "./components";
import QuickStartTemplates from "./components/QuickStartTemplates";
import { useChatMessages } from "./hooks";
import {
  chatWithAI,
  generateAdDetails,
  generatePrompt,
  getProjectDetails,
  regenerateAiImages
} from "../../services/userService";
import Utils from "../../utility";
import Modal from "../../common/components/popup/modal";
import LoadingPopup from "../../common/components/popup/LoadingPopup";
import AiGeneratedPopup from "../welcome/project/aiGeneratedImages";

const BrandChatInterface = ({ brandDetails }) => {
  const { messages, isTyping, setIsTyping, addUserMessage, addSystemMessage } =
    useChatMessages([
      {
        id: "1",
        type: "system",
        content:
          "Hi! I'm AdPerfect AI, your project creation assistant. Tell me what kind of ad or social post you'd like to create, and I'll help set everything up.",
        timestamp: new Date()
      }
    ]);

  const [conversationHistory, setConversationHistory] = useState([]);
  const [collectedData, setCollectedData] = useState({});
  const [imagePopup, setImagePopup] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingComplete, setPendingComplete] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  // State for AI-generated images flow
  const [projectDetailsGeneratedData, setProjectDetailsGeneratedData] =
    useState({});
  const [projectDetailsState, setProjectDetailsState] = useState({});

  const brandContext = {
    brandName: brandDetails?.brandName,
    businessModel: brandDetails?.businessModel,
    businessType: brandDetails?.businessType,
    industry: brandDetails?.industry,
    brandProblemSolve: brandDetails?.discovery?.problem,
    solutionOffered: brandDetails?.discovery?.solution,
    voiceTone: brandDetails?.discovery?.tone,
    brandMainCompetitor: brandDetails?.discovery?.competitors,
    differentiatingFactor: brandDetails?.discovery?.values?.join(", ")
  };

  const handlePopupClose = useCallback(() => {
    setImagePopup(null);
    setSearchQuery("");
  }, []);

  const buildPayload = useCallback(
    (finalData) => ({
      typeOfAd: finalData.adType === "textAd" ? "text" : "image",
      projectName: finalData.projectName,
      businessName: brandDetails?.brandName,
      businessModel: brandDetails?.businessModel,
      industry: brandDetails?.businessType,
      brandProblemSolve: brandDetails?.discovery?.problem,
      solutionOffered: brandDetails?.discovery?.solution,
      voiceTone: brandDetails?.discovery?.tone,
      brandMainCompetitor: brandDetails?.discovery?.competitors,
      differentiatingFactor: brandDetails?.discovery?.values?.join(", "),
      projectType: finalData.projectType,
      platformName: finalData.platformName,
      assetType: finalData.projectType === "ad" ? "Ad" : "Social Post",
      projectGoal: finalData.goal,
      projectDescription: finalData.projectDescription,
      targetAudience: {
        gender: finalData.gender,
        interests: [finalData.interest],
        minAge: Number(finalData.minAge) || 18,
        maxAge: Number(String(finalData.maxAge).replace("+", "")) || 65
      },
      selectedImage: finalData.selectedImage || "",
      selectedImageOption: finalData.imageSource || "ownImage",
      brandId: brandDetails?.id
    }),
    [brandDetails]
  );

  // AI images are generated in the background after the project is created.
  // Poll the project until the images land (or generation fails) and feed
  // them into the open AI image popup, which shows skeletons until then.
  const pollForGeneratedImages = useCallback(async (projectId) => {
    const maxAttempts = 80; // ~4 min at 3s
    const intervalMs = 3000;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));

      const [error, response] = await Utils.parseResponse(
        getProjectDetails(projectId)
      );
      if (error || !response) continue;

      const project = response?.responseData;
      const status = project?.imageGenerationStatus;

      if (status === "completed" || project?.images?.length > 0) {
        setProjectDetailsGeneratedData((prev) => ({
          ...prev,
          promptRes: {
            ...prev?.promptRes,
            images: project.images
          }
        }));
        return;
      }

      if (status === "failed") {
        Utils.failureToastMessage(
          project?.imageGenerationError ||
            "Image generation failed. Please try regenerating."
        );
        return;
      }
    }

    Utils.failureToastMessage(
      "Image generation is taking longer than expected. Please try regenerating."
    );
  }, []);

  const handleProjectComplete = useCallback(
    async (finalData) => {
      const payload = buildPayload(finalData);

      setShowLoading(true);

      if (finalData.adType === "textAd") {
        const [error, response] = await Utils.parseResponse(
          generatePrompt(payload)
        );
        setShowLoading(false);
        if (!error && response) {
          const projectId = response.responseData?.id || response.id;
          window.location.href = `/dashboard/brand/details/project/finalReview/${projectId}`;
        } else {
          addSystemMessage(
            "There was an error creating your project. Please try again."
          );
        }
      } else if (finalData.imageSource === "aiGeneratedImage") {
        const [error, response] = await Utils.parseResponse(
          generateAdDetails(payload)
        );
        setShowLoading(false);
        if (!error && response) {
          const projectData = response?.responseData || response;
          setProjectDetailsGeneratedData(projectData);
          setProjectDetailsState(finalData);
          setImagePopup("aiGeneratedImage");
          const projectId =
            projectData?.promptRes?._id || projectData?.promptRes?.id;
          if (projectId) {
            pollForGeneratedImages(projectId);
          }
        } else {
          addSystemMessage(
            "There was an error creating your project. Please try again."
          );
        }
      } else {
        const [error, response] = await Utils.parseResponse(
          generateAdDetails(payload)
        );
        setShowLoading(false);
        if (!error && response) {
          const projectId =
            response.responseData?.promptRes?._id ||
            response.responseData?.promptRes?.id ||
            response.responseData?.id ||
            response.id;
          const brandId = brandDetails?.id;
          window.location.href = `/dashboard/brand/details/${brandId}/${projectId}`;
        } else {
          addSystemMessage(
            "There was an error creating your project. Please try again."
          );
        }
      }
    },
    [buildPayload, addSystemMessage, pollForGeneratedImages]
  );

  // Called when user selects/uploads an image from a popup
  const handleImageSelected = useCallback(
    (imageUrl) => {
      const updatedData = { ...collectedData, selectedImage: imageUrl };
      setCollectedData(updatedData);

      // If all data was collected and we were waiting on an image, complete now
      if (pendingComplete) {
        setPendingComplete(false);
        setImagePopup(null);
        handleProjectComplete(updatedData);
      }
    },
    [collectedData, pendingComplete, handleProjectComplete]
  );

  const regenerateImages = useCallback(async () => {
    try {
      const projectId =
        projectDetailsGeneratedData?.promptRes?._id ||
        projectDetailsGeneratedData?.promptRes?.id;

      if (!projectId) return;

      // Clear images so the popup falls back to skeletons while regenerating.
      setProjectDetailsGeneratedData((prev) => ({
        ...prev,
        promptRes: {
          ...prev?.promptRes,
          images: []
        }
      }));

      const [error] = await Utils.parseResponse(regenerateAiImages(projectId));
      if (error) {
        Utils.failureToastMessage(
          error?.response?.data?.message || "Some error occurred"
        );
        return;
      }

      // Regeneration runs in the background; poll for the new images.
      pollForGeneratedImages(projectId);
    } catch (error) {
      Utils.failureToastMessage("Failed to regenerate images");
    }
  }, [projectDetailsGeneratedData, pollForGeneratedImages]);

  const handleSendMessage = useCallback(
    async (content) => {
      setHasStarted(true);
      addUserMessage(content);
      setIsTyping(true);

      const updatedHistory = [
        ...conversationHistory,
        { role: "user", content }
      ];

      try {
        const [error, response] = await Utils.parseResponse(
          chatWithAI(updatedHistory, brandContext)
        );

        if (error || !response) {
          addSystemMessage(
            "Sorry, I had trouble processing that. Could you try again?"
          );
          setIsTyping(false);
          return;
        }

        const data = response.responseData || response;

        const newHistory = [
          ...updatedHistory,
          { role: "assistant", content: JSON.stringify(data) }
        ];
        setConversationHistory(newHistory);

        if (data.collectedData) {
          setCollectedData((prev) => {
            const merged = { ...prev };
            Object.entries(data.collectedData).forEach(([key, value]) => {
              if (value !== null && value !== undefined) {
                merged[key] = value;
              }
            });
            return merged;
          });
        }

        addSystemMessage(data.message);

        // Only upload/unsplash need to defer completion (user must pick image first).
        // AI images are generated server-side AFTER project creation, so they don't block.
        const needsImageFirst =
          data.action === "triggerUpload" || data.action === "triggerUnsplash";

        if (data.action === "triggerUpload") {
          setImagePopup("upload");
        } else if (data.action === "triggerUnsplash") {
          setImagePopup("unsplash");
        } else if (data.action === "triggerAiImages") {
          // Record the choice — AI popup shows after project creation.
          setCollectedData((prev) => ({
            ...prev,
            imageSource: "aiGeneratedImage"
          }));
        }

        if (data.isComplete && !needsImageFirst) {
          // Ready to create project. For AI images this triggers creation
          // then shows the AI image picker popup with generated results.
          const finalData = { ...collectedData };
          Object.entries(data.collectedData).forEach(([key, value]) => {
            if (value !== null) finalData[key] = value;
          });
          // If AI images action fired in this same response, include it
          if (data.action === "triggerAiImages") {
            finalData.imageSource = "aiGeneratedImage";
          }
          handleProjectComplete(finalData);
        } else if (data.isComplete && needsImageFirst) {
          // Upload/unsplash: wait for user to select image, then complete
          setPendingComplete(true);
        }
      } catch (err) {
        addSystemMessage("Something went wrong. Please try again.");
      } finally {
        setIsTyping(false);
      }
    },
    [
      conversationHistory,
      brandContext,
      collectedData,
      addUserMessage,
      addSystemMessage,
      setIsTyping,
      handleProjectComplete
    ]
  );

  const renderImagePopup = () => {
    switch (imagePopup) {
      case "upload":
        return (
          <Modal
            heading={"Upload Image"}
            content={"Upload your own image to use for your ads.*"}
            buttonText={"UPLOAD IMAGE"}
            closeModal={handlePopupClose}
            isUpload={true}
            onSelect={(url) => {
              handleImageSelected(url);
              if (!pendingComplete) {
                setImagePopup(null);
                handleSendMessage("I've uploaded my image.");
              }
            }}
            buttonClassName="my-4 flex size-auto cursor-pointer items-center justify-center rounded-2xl bg-green-12 w-87.5 h-[45px] flex items-align font-InterBold text-ft14-21 text-white"
            contentClassName="mt-2.5 w-70per text-center font-InterRegular text-ft14-20 text-grey-14"
          />
        );

      case "unsplash":
        return (
          <Modal
            searchQuery={searchQuery}
            heading={"Image Library"}
            content={
              <>
                <p>
                  You can choose from any of the royalty-free images below to
                  use for your ad
                </p>
                <div className="relative m-auto mt-2 flex h-10.2 w-[341px] cursor-pointer items-center justify-center rounded-3xl border-0.5px bg-white p-2">
                  <input
                    className="h-8.5 w-[341px] rounded-3xl border-none p-2 outline-none"
                    placeholder="Search Images"
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                  />
                </div>
              </>
            }
            buttonText={"SELECT IMAGE"}
            closeModal={handlePopupClose}
            onSelect={(url) => {
              handleImageSelected(url);
              if (!pendingComplete) {
                setImagePopup(null);
                handleSendMessage("I've selected my image.");
              }
            }}
            isImages={true}
            buttonClassName="mb-10 cursor-pointer items-center justify-center rounded-2xl bg-green-12 px-30.7 py-3 font-InterBold text-ft14-21 text-white absolute bottom-0 left-[50%] translate-x-[-50%]"
            contentClassName="mt-2.5 w-70per text-center font-InterRegular text-ft14-20 text-grey-26"
            headingClassName="mt-27px font-InterBold text-ft32-41.6 text-grey-24"
          />
        );

      case "aiGeneratedImage":
        return (
          <AiGeneratedPopup
            setStep={() => {
              // After selecting an AI image, show loading then navigate
              setShowLoading(true);
              const projectId =
                projectDetailsGeneratedData?.promptRes?._id ||
                projectDetailsGeneratedData?.promptRes?.id;
              if (projectId) {
                const brandId = brandDetails?.id;
                window.location.href = `/dashboard/brand/details/${brandId}/${projectId}`;
              }
            }}
            setCurrentPopup={(val) => {
              if (!val) setImagePopup(null);
            }}
            setProjectDetailState={setProjectDetailsState}
            projectDetailsState={projectDetailsState}
            projectDetailsGeneratedData={projectDetailsGeneratedData}
            regenerateImages={regenerateImages}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex h-full w-[60%] max-w-[1200px] flex-col">
        <ChatArea messages={messages} isTyping={isTyping} />

        {!hasStarted && <QuickStartTemplates onSelect={handleSendMessage} />}

        <ChatInput
          onSend={handleSendMessage}
          disabled={isTyping}
          placeholder="Describe what you want to create..."
        />

        {renderImagePopup()}
      </div>

      <LoadingPopup
        adType={collectedData.adType || "imageAd"}
        imageType={collectedData.imageSource}
        projectData={{
          platformName: collectedData.platformName,
          projectName: collectedData.projectName
        }}
        isVisible={showLoading}
      />
    </div>
  );
};

export default BrandChatInterface;
